import { InjectionToken, Injectable, Inject, Optional, Directive, Input, Output, EventEmitter, NgZone, NgModule, Renderer2, ElementRef, defineInjectable, inject } from '@angular/core';
import hljs from 'highlight.js/lib/highlight.js';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,uselessCode} checked by tsc
 */
/** @type {?} */
var HIGHLIGHT_OPTIONS = new InjectionToken('HIGHLIGHT_OPTIONS');

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,uselessCode} checked by tsc
 */
var HighlightJS = /** @class */ (function () {
    function HighlightJS(options) {
        var _this = this;
        if (options) {
            // Register HighlightJS languages
            options.languages().map(function (language) {
                return _this.registerLanguage(language.name, language.func);
            });
            if (options.config) {
                // Set global config if present
                this.configure(options.config);
            }
        }
        // Throw an error if no languages were registered.
        if (this.listLanguages().length < 1) {
            throw new Error('[HighlightJS]: No languages were registered!');
        }
    }
    /**
     * Core highlighting function.
     * @param name Accepts a language name, or an alias
     * @param value A string with the code to highlight.
     * @param ignore_illegals When present and evaluates to a true value, forces highlighting to finish
     * even in case of detecting illegal syntax for the language instead of throwing an exception.
     * @param continuation An optional mode stack representing unfinished parsing.
     * When present, the function will restart parsing from this state instead of initializing a new one
     */
    /**
     * Core highlighting function.
     * @param {?} name Accepts a language name, or an alias
     * @param {?} value A string with the code to highlight.
     * @param {?} ignore_illegals When present and evaluates to a true value, forces highlighting to finish
     * even in case of detecting illegal syntax for the language instead of throwing an exception.
     * @param {?=} continuation An optional mode stack representing unfinished parsing.
     * When present, the function will restart parsing from this state instead of initializing a new one
     * @return {?}
     */
    HighlightJS.prototype.highlight = /**
     * Core highlighting function.
     * @param {?} name Accepts a language name, or an alias
     * @param {?} value A string with the code to highlight.
     * @param {?} ignore_illegals When present and evaluates to a true value, forces highlighting to finish
     * even in case of detecting illegal syntax for the language instead of throwing an exception.
     * @param {?=} continuation An optional mode stack representing unfinished parsing.
     * When present, the function will restart parsing from this state instead of initializing a new one
     * @return {?}
     */
    function (name, value, ignore_illegals, continuation) {
        return hljs.highlight(name, value, ignore_illegals, continuation);
    };
    /**
     * Highlighting with language detection.
     * @param value Accepts a string with the code to highlight
     * @param languageSubset An optional array of language names and aliases restricting detection to only those languages.
     * The subset can also be set with configure, but the local parameter overrides the option if set.
     */
    /**
     * Highlighting with language detection.
     * @param {?} value Accepts a string with the code to highlight
     * @param {?} languageSubset An optional array of language names and aliases restricting detection to only those languages.
     * The subset can also be set with configure, but the local parameter overrides the option if set.
     * @return {?}
     */
    HighlightJS.prototype.highlightAuto = /**
     * Highlighting with language detection.
     * @param {?} value Accepts a string with the code to highlight
     * @param {?} languageSubset An optional array of language names and aliases restricting detection to only those languages.
     * The subset can also be set with configure, but the local parameter overrides the option if set.
     * @return {?}
     */
    function (value, languageSubset) {
        return hljs.highlightAuto(value, languageSubset);
    };
    /**
     * Post-processing of the highlighted markup.
     * Currently consists of replacing indentation TAB characters and using <br> tags instead of new-line characters.
     * Options are set globally with configure.
     * @param value Accepts a string with the highlighted markup
     */
    /**
     * Post-processing of the highlighted markup.
     * Currently consists of replacing indentation TAB characters and using <br> tags instead of new-line characters.
     * Options are set globally with configure.
     * @param {?} value Accepts a string with the highlighted markup
     * @return {?}
     */
    HighlightJS.prototype.fixMarkup = /**
     * Post-processing of the highlighted markup.
     * Currently consists of replacing indentation TAB characters and using <br> tags instead of new-line characters.
     * Options are set globally with configure.
     * @param {?} value Accepts a string with the highlighted markup
     * @return {?}
     */
    function (value) {
        return hljs.fixMarkup(value);
    };
    /**
     * Applies highlighting to a DOM node containing code.
     * The function uses language detection by default but you can specify the language in the class attribute of the DOM node.
     * See the class reference for all available language names and aliases.
     * @param block The element to apply highlight on.
     */
    /**
     * Applies highlighting to a DOM node containing code.
     * The function uses language detection by default but you can specify the language in the class attribute of the DOM node.
     * See the class reference for all available language names and aliases.
     * @param {?} block The element to apply highlight on.
     * @return {?}
     */
    HighlightJS.prototype.highlightBlock = /**
     * Applies highlighting to a DOM node containing code.
     * The function uses language detection by default but you can specify the language in the class attribute of the DOM node.
     * See the class reference for all available language names and aliases.
     * @param {?} block The element to apply highlight on.
     * @return {?}
     */
    function (block) {
        hljs.highlightBlock(block);
    };
    /**
     * Configures global options:
     * @param config
     */
    /**
     * Configures global options:
     * @param {?} config
     * @return {?}
     */
    HighlightJS.prototype.configure = /**
     * Configures global options:
     * @param {?} config
     * @return {?}
     */
    function (config) {
        hljs.configure(config);
    };
    /**
     * Applies highlighting to all <pre><code>..</code></pre> blocks on a page.
     */
    /**
     * Applies highlighting to all <pre><code>..</code></pre> blocks on a page.
     * @return {?}
     */
    HighlightJS.prototype.initHighlighting = /**
     * Applies highlighting to all <pre><code>..</code></pre> blocks on a page.
     * @return {?}
     */
    function () {
        hljs.initHighlighting();
    };
    /**
     * Adds new language to the library under the specified name. Used mostly internally.
     * @param name A string with the name of the language being registered
     * @param language A function that returns an object which represents the language definition.
     * The function is passed the hljs object to be able to use common regular expressions defined within it.
     */
    /**
     * Adds new language to the library under the specified name. Used mostly internally.
     * @param {?} name A string with the name of the language being registered
     * @param {?} language A function that returns an object which represents the language definition.
     * The function is passed the hljs object to be able to use common regular expressions defined within it.
     * @return {?}
     */
    HighlightJS.prototype.registerLanguage = /**
     * Adds new language to the library under the specified name. Used mostly internally.
     * @param {?} name A string with the name of the language being registered
     * @param {?} language A function that returns an object which represents the language definition.
     * The function is passed the hljs object to be able to use common regular expressions defined within it.
     * @return {?}
     */
    function (name, language) {
        hljs.registerLanguage(name, language);
    };
    /**
     * @return The languages names list.
     */
    /**
     * @return {?} The languages names list.
     */
    HighlightJS.prototype.listLanguages = /**
     * @return {?} The languages names list.
     */
    function () {
        return hljs.listLanguages();
    };
    /**
     * Looks up a language by name or alias.
     * @param name Language name
     * @return The language object if found, undefined otherwise.
     */
    /**
     * Looks up a language by name or alias.
     * @param {?} name Language name
     * @return {?} The language object if found, undefined otherwise.
     */
    HighlightJS.prototype.getLanguage = /**
     * Looks up a language by name or alias.
     * @param {?} name Language name
     * @return {?} The language object if found, undefined otherwise.
     */
    function (name) {
        return hljs.getLanguage(name);
    };
    HighlightJS.decorators = [
        { type: Injectable, args: [{
                    providedIn: 'root'
                },] }
    ];
    /** @nocollapse */
    HighlightJS.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [HIGHLIGHT_OPTIONS,] }] }
    ]; };
    /** @nocollapse */ HighlightJS.ngInjectableDef = defineInjectable({ factory: function HighlightJS_Factory() { return new HighlightJS(inject(HIGHLIGHT_OPTIONS, 8)); }, token: HighlightJS, providedIn: "root" });
    return HighlightJS;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,uselessCode} checked by tsc
 */
var Highlight = /** @class */ (function () {
    function Highlight(_hljs, _zone) {
        this._hljs = _hljs;
        this._zone = _zone;
        /**
         * Stream that emits when code string is highlighted
         */
        this.highlighted = new EventEmitter();
    }
    /**
     * @param {?} changes
     * @return {?}
     */
    Highlight.prototype.ngOnChanges = /**
     * @param {?} changes
     * @return {?}
     */
    function (changes) {
        if (changes['code'] &&
            changes['code'].currentValue !== changes['code'].previousValue) {
            this.highlightElement(this.code, this.languages);
        }
    };
    /**
     * Highlighting with language detection and fix markup.
     * @param value Accepts a string with the code to highlight
     * @param languageSubset An optional array of language names and aliases restricting detection to only those languages.
     * The subset can also be set with configure, but the local parameter overrides the option if set.
     */
    /**
     * Highlighting with language detection and fix markup.
     * @param {?} code
     * @param {?=} languages
     * @return {?}
     */
    Highlight.prototype.highlightElement = /**
     * Highlighting with language detection and fix markup.
     * @param {?} code
     * @param {?=} languages
     * @return {?}
     */
    function (code, languages) {
        var _this = this;
        this._zone.runOutsideAngular(function () {
            /** @type {?} */
            var res = _this._hljs.highlightAuto(code, languages);
            _this.highlightedCode = res.value;
            _this.highlighted.emit(res);
        });
    };
    Highlight.decorators = [
        { type: Directive, args: [{
                    host: {
                        '[class.hljs]': 'true',
                        '[innerHTML]': 'highlightedCode'
                    },
                    selector: '[highlight]'
                },] }
    ];
    /** @nocollapse */
    Highlight.ctorParameters = function () { return [
        { type: HighlightJS },
        { type: NgZone }
    ]; };
    Highlight.propDecorators = {
        languages: [{ type: Input }],
        code: [{ type: Input, args: ['highlight',] }],
        highlighted: [{ type: Output }]
    };
    return Highlight;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,uselessCode} checked by tsc
 */
var HighlightChildren = /** @class */ (function () {
    function HighlightChildren(_zone, _el, _hljs, _renderer) {
        this._zone = _zone;
        this._el = _el;
        this._hljs = _hljs;
        this._renderer = _renderer;
    }
    /**
     * @return {?}
     */
    HighlightChildren.prototype.ngAfterContentInit = /**
     * @return {?}
     */
    function () {
        this.highlightChildren(this.selector);
    };
    /**
     * Highlight a code block
     * @param el Code block element
     */
    /**
     * Highlight a code block
     * @param {?} el Code block element
     * @return {?}
     */
    HighlightChildren.prototype.highlightElement = /**
     * Highlight a code block
     * @param {?} el Code block element
     * @return {?}
     */
    function (el) {
        this._hljs.highlightBlock(el);
        this._renderer.addClass(el, 'hljs');
    };
    /**
     * Highlight multiple code blocks
     * @param selector elements selector
     */
    /**
     * Highlight multiple code blocks
     * @param {?} selector elements selector
     * @return {?}
     */
    HighlightChildren.prototype.highlightChildren = /**
     * Highlight multiple code blocks
     * @param {?} selector elements selector
     * @return {?}
     */
    function (selector) {
        var _this = this;
        this._zone.runOutsideAngular(function () {
            /** @type {?} */
            var elementsToHighlight = _this._el.nativeElement.querySelectorAll(selector || 'code');
            elementsToHighlight.forEach(function (element) {
                // Highlight element when text is present
                /** @type {?} */
                var observer = new MutationObserver(function () {
                    if (element.childNodes.length === 1 &&
                        element.childNodes[0].nodeName === '#text') {
                        _this.highlightElement(element);
                    }
                    observer.disconnect();
                });
                observer.observe(element, { childList: true });
            });
        });
    };
    HighlightChildren.decorators = [
        { type: Directive, args: [{
                    selector: '[highlightChildren]'
                },] }
    ];
    /** @nocollapse */
    HighlightChildren.ctorParameters = function () { return [
        { type: NgZone },
        { type: ElementRef },
        { type: HighlightJS },
        { type: Renderer2 }
    ]; };
    HighlightChildren.propDecorators = {
        selector: [{ type: Input, args: ['highlightChildren',] }]
    };
    return HighlightChildren;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,uselessCode} checked by tsc
 */
var HighlightModule = /** @class */ (function () {
    function HighlightModule() {
    }
    /**
     * @param {?} options
     * @return {?}
     */
    HighlightModule.forRoot = /**
     * @param {?} options
     * @return {?}
     */
    function (options) {
        return {
            ngModule: HighlightModule,
            providers: [
                { provide: HIGHLIGHT_OPTIONS, useValue: options }
            ]
        };
    };
    HighlightModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [Highlight, HighlightChildren],
                    exports: [Highlight, HighlightChildren]
                },] }
    ];
    return HighlightModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,uselessCode} checked by tsc
 */

export { Highlight, HIGHLIGHT_OPTIONS, HighlightModule, HighlightJS, HighlightChildren };

//# sourceMappingURL=data:application/json;charset=utf-8;base64,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